import { InsightFilter } from '@api/api/insights';
import { getConditionIcons } from '@terminal/components/insight-icons/utils';
import { useInsightDrawer } from '@terminal/hooks/use-insight-drawer/useInsightDrawer';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { getExploreDirection } from '@terminal/views/dashboard/whats-new-widget/components/insight-card/utils';
import { TruncateCombinedIcons } from '@terminal/views/explore/components/truncate-combined-icons/TruncateCombinedIcons';
import { TruncateSingleIcons } from '@terminal/views/explore/components/truncate-single-icons/TruncateSingleIcons';

export type InsightsIconProps = {
  insight: InsightFilter;
  isDisabled: boolean;
  variant?: 'chart' | 'assets-table';
  source: PosthogTrackingSource;
};

export const InsightsIcon = ({
  insight,
  isDisabled,
  variant = 'chart',
  source,
}: InsightsIconProps) => {
  const { openDrawer } = useInsightDrawer();
  const { data: entityData } = useEntity(insight.metadata.entity);

  const isCombined = insight.metadata.kind === 'combination';

  const handleSelect = (insightId: string) => {
    openDrawer({
      id: insightId,
      entityTag: insight.metadata.entity,
      trackingProperties: { source },
    });
  };

  const direction = getExploreDirection(insight.metadata.direction);

  if (isCombined) {
    const limitedConditions = getConditionIcons({
      kind: insight.metadata.kind,
      conditions: insight.metadata.conditions,
    });
    const displayedInsights = limitedConditions.map(c => ({
      ...c,
      name: insight.card.name,
      insightId: insight.id,
      median: insight.metadata.median_return,
      createdAt: insight.created_at,
      direction,
      horizon: insight.metadata.horizon,
      episodesCount: insight.metadata.episode_count,
      stars: insight.metadata.stars,
      type: insight.metadata.type,
      matched: !isDisabled,
    }));

    return (
      <TruncateCombinedIcons
        displayedInsights={[displayedInsights]}
        shouldTruncate={false}
        entity={entityData?.data}
        handleSelect={!isDisabled ? handleSelect : undefined}
        variant={variant}
        source={source}
      />
    );
  }

  const formattedInsight = {
    card: { name: insight.card.name },
    id: insight.id,
    median: insight.metadata.median_return,
    created_at: insight.created_at,
    direction,
    matched: !isDisabled,
    best_horizon: insight.metadata.horizon,
    conditions: insight.metadata.conditions,
    episode_count: insight.metadata.episode_count,
    stars: insight.metadata.stars,
    kind: insight.metadata.kind,
  };

  return (
    <TruncateSingleIcons
      insights={[formattedInsight]}
      entity={entityData?.data}
      handleSelect={!isDisabled ? handleSelect : undefined}
      variant={variant}
      source={source}
    />
  );
};
